import { Button } from '@/design-system';
import type { ConflictRecord } from '@/shared/offline';

import { surveyCopy } from '../copy';
import type { TemplateGraph } from '../engine';

export interface ConflictNoticeProps {
    conflicts: readonly ConflictRecord[];
    graph: TemplateGraph;
    onAcknowledge: (ids: readonly string[]) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/** Waar ging het over: een vraag, een aantal of iets anders. */
function subjectOf(conflict: ConflictRecord, graph: TemplateGraph): string {
    const payload = isRecord(conflict.localPayload) ? conflict.localPayload : {};

    if (conflict.entity === 'inspection_answer' && typeof payload.question_id === 'number') {
        const question = graph.questionsById.get(payload.question_id);

        if (question) {
            return surveyCopy.conflicts.subjectAnswer(question.question.label);
        }
    }

    if (conflict.entity === 'multi_count' && typeof payload.container_node_id === 'number') {
        const node = graph.nodesById.get(payload.container_node_id);

        if (node) {
            return surveyCopy.conflicts.subjectCount(node.node.name);
        }
    }

    return surveyCopy.conflicts.subjectOther;
}

/**
 * De reden die de inspecteur iets zegt. Bij een weigering op de inhoud staat die in
 * de veldfouten ("Niet alle antwoordopties horen bij deze vraag."); de algemene
 * `detail` wijst dan alleen naar dat veld.
 */
function reasonOf(conflict: ConflictRecord): string {
    const errors = conflict.problem?.errors;
    const fieldError = isRecord(errors)
        ? Object.values(errors)
              .flatMap((messages) => (Array.isArray(messages) ? messages : []))
              .find((message): message is string => typeof message === 'string')
        : undefined;

    return (
        fieldError ??
        conflict.problem?.detail ??
        conflict.problem?.title ??
        surveyCopy.conflicts.noReason
    );
}

function conflictMessage(conflict: ConflictRecord, graph: TemplateGraph): string {
    /*
     * De server zegt zelf waar het staat: de vraag of het onderdeel bestaat in de
     * nieuwe vragenlijst vaak niet meer, dus deze graaf kent de naam niet.
     */
    if (conflict.kind === 'notice') {
        return conflict.notice?.detail ?? surveyCopy.conflicts.noticeFallback;
    }

    if (conflict.kind === 'photo') {
        const payload = isRecord(conflict.localPayload) ? conflict.localPayload : {};
        const fileName = typeof payload.file_name === 'string' ? payload.file_name : '';

        return payload.action === 'delete'
            ? surveyCopy.conflicts.photoDelete(fileName, reasonOf(conflict))
            : surveyCopy.conflicts.photo(fileName, reasonOf(conflict));
    }

    return conflict.kind === 'conflict'
        ? surveyCopy.conflicts.replaced(subjectOf(conflict, graph))
        : surveyCopy.conflicts.rejected(subjectOf(conflict, graph), reasonOf(conflict));
}

/**
 * Wat de server niet overnam, zichtbaar boven de vragen. Een vervangen
 * antwoord is geen fout van de inspecteur: de melding is amber, geen dialoog,
 * en de opname gaat gewoon door.
 */
export function ConflictNotice({ conflicts, graph, onAcknowledge }: ConflictNoticeProps) {
    if (conflicts.length === 0) {
        return null;
    }

    /* De kop telt wat echt niet overkwam; bestaat de lijst alleen uit meldingen, dan zegt hij dat rustig. */
    const failures = conflicts.filter((conflict) => conflict.kind !== 'notice').length;
    const title =
        failures === 0 ? surveyCopy.conflicts.noticeTitle : surveyCopy.conflicts.title(failures);

    return (
        <section
            role="status"
            aria-label={title}
            className="mb-[14px] flex flex-wrap items-start justify-between gap-3 rounded-[9px] border border-[var(--warn-border)] bg-[var(--warn-tint)] px-3 py-[11px]"
        >
            <div className="min-w-0 flex-1">
                <p className="m-0 text-[13px] leading-[1.4] font-medium text-[var(--warn-text)]">
                    {title}
                </p>
                <ul className="m-0 mt-1 list-none p-0">
                    {conflicts.map((conflict) => (
                        <li
                            key={conflict.id}
                            className="text-[13px] leading-[1.5] text-[var(--text-primary)]"
                        >
                            {conflictMessage(conflict, graph)}
                        </li>
                    ))}
                </ul>
            </div>
            <Button
                variant="outline"
                size="md"
                onClick={() => onAcknowledge(conflicts.map((conflict) => conflict.id))}
            >
                {surveyCopy.conflicts.acknowledge}
            </Button>
        </section>
    );
}
